/**
 * OCT28 RECOVERY #4 (founder audit 2026-11-02) — what Birdseye's AUTOMATIC fit frames.
 *
 * The field complaint was the bay map "scrambled" on a phone. Reproduced in a real browser with Floyd's floor plan
 * (8 bays at x 3800–5500, the lot's spots at x 20–1020 — about 5,600 layout units wide): the bays' ARRANGEMENT was
 * always intact (one uniform layout→screen map), but on every phone and tablet size the automatic fit was clamped
 * at zoom 0.2 and centred on the whole lot, which put every bay off the right edge of the screen. The Oct 29 change
 * only collapsed the side panel, so the bays stayed off screen.
 *
 * Rule: fit everything (the desktop behaviour, unchanged) unless that would draw the bays too small to read or tap;
 * then frame the BAYS — the working floor — and leave the lot reachable by panning or "Fit to Screen".
 * Canonical coordinates are never changed; this only chooses the camera.
 */
import type { FitOptions, MapRect } from '@/hooks/useMapCamera';

/** A bay narrower than this on screen cannot show its ticket and is hard to tap. */
export const BAY_MIN_LEGIBLE_PX = 64;
/** Screen-pixel margin around the bays when they are framed. */
export const BAY_FRAME_PAD_PX = 16;

/** The scale the camera's whole-layout fit uses (its historical 60 layout-unit margin), before any clamp. */
export function wholeLayoutScale(viewW: number, viewH: number, items: readonly MapRect[]): number {
  if (items.length === 0 || viewW <= 0 || viewH <= 0) return 1;
  const minX = Math.min(...items.map((i) => i.x));
  const maxX = Math.max(...items.map((i) => i.x + i.w));
  const minY = Math.min(...items.map((i) => i.y));
  const maxY = Math.max(...items.map((i) => i.y + i.h));
  return Math.min(viewW / (maxX - minX + 120), viewH / (maxY - minY + 120));
}

export function birdseyeAutoFit(viewW: number, viewH: number, items: readonly MapRect[], bays: readonly MapRect[]): FitOptions {
  if (bays.length === 0) return {};
  const narrowestBay = Math.min(...bays.map((b) => b.w));
  if (narrowestBay * wholeLayoutScale(viewW, viewH, items) >= BAY_MIN_LEGIBLE_PX) return {};
  return { rects: bays, padPx: BAY_FRAME_PAD_PX };
}
